import { api } from '..';
import { stripApiPrefix, withParams } from '../paths';
import type {
  GenericWeeklyReportResponseDto,
  WeeklyReportDto,
  UpdateWeeklyReportDraftDto,
} from '@/types/generated/schemas';
import { WeeklyReportDraft } from '@/modules/peer-support/weekly-report/model/types';

// The API identifies periods by the stored ISO 8601 periodStart instant.
// Preserve the complete timestamp, including its time and timezone.
export type WeeklyReportPeriodStart = string;
export type WeeklyReportPeriod = {
  periodStart: WeeklyReportPeriodStart;
  periodEnd: string;
  windowEnd: string;
  status: 'open' | 'expired' | 'already-submitted';
  submittedAt?: string | null;
  existingContactCount: number;
  draft: WeeklyReportDraft;
};
const periodEndpoint = (periodStart?: WeeklyReportPeriodStart) =>
  periodStart
    ? withParams('/api/v1/peersupport/weekly-report/{periodStart}', { periodStart })
    : stripApiPrefix('/api/v1/peersupport/weekly-report/current');

export const getWeeklyReportPeriod = async (periodStart?: WeeklyReportPeriodStart): Promise<WeeklyReportPeriod> => {
  const { data } = await api.get<GenericWeeklyReportResponseDto>(periodEndpoint(periodStart));
  return toWeeklyReportPeriod(data.data);
};

// The token authorizes access to its report, including when no user is signed in.
export const exchangeWeeklyReportToken = async (token: string): Promise<WeeklyReportPeriod> => {
  const { data } = await api.get<GenericWeeklyReportResponseDto>(
    withParams('/api/v1/peersupport/weekly-report/link/{token}', { token }),
  );
  return toWeeklyReportPeriod(data.data);
};

const toWeeklyReportPeriod = (report: WeeklyReportDto): WeeklyReportPeriod => {
  const topics: WeeklyReportDraft['topics'] = {};
  for (const { domain, subdomain, count } of report.topics) {
    (topics[domain] ??= {})[subdomain] = count;
  }
  return {
    periodStart: report.periodStart,
    periodEnd: report.periodEnd,
    windowEnd: report.windowEnd,
    submittedAt: report.submittedAt,
    status: report.status !== 'draft' ? 'already-submitted' : report.expired || report.readOnly ? 'expired' : 'open',
    existingContactCount: report.previouslyRecorded.length,
    draft: {
      contactCount: report.newContactCount ?? 0,
      domains: Object.fromEntries(report.domains.map(({ domain, count }) => [domain, count])),
      topics,
      outcomes: Object.fromEntries(report.outcomes.map(({ outcome, count }) => [outcome, count])),
    },
  };
};
export const submitWeeklyReport = async (
  periodStart: WeeklyReportPeriodStart,
  report: WeeklyReportDraft,
  token?: string,
) => {
  const url = token
    ? withParams('/api/v1/peersupport/weekly-report/link/{token}', { token })
    : periodEndpoint(periodStart);
  const payload: UpdateWeeklyReportDraftDto = {
    newContactCount: report.contactCount,
    domains: Object.entries(report.domains).map(([domain, count]) => ({
      domain: domain as WeeklyReportDto['domains'][number]['domain'],
      count,
    })),
    topics: Object.entries(report.topics).flatMap(([domain, topics]) =>
      Object.entries(topics).map(([subdomain, count]) => ({
        domain: domain as WeeklyReportDto['topics'][number]['domain'],
        subdomain,
        count,
      })),
    ),
    outcomes: Object.entries(report.outcomes).map(([outcome, count]) => ({
      outcome: outcome as WeeklyReportDto['outcomes'][number]['outcome'],
      count,
    })),
  };
  await api.put<GenericWeeklyReportResponseDto>(url, payload);
  await api.post<GenericWeeklyReportResponseDto>(
    token
      ? withParams('/api/v1/peersupport/weekly-report/link/{token}/submit', { token })
      : withParams('/api/v1/peersupport/weekly-report/{periodStart}/submit', { periodStart }),
  );
};
